import React, { useState } from 'react';
import { 
    FaExternalLinkAlt, 
    FaGlobeAmericas, 
    FaGavel, 
    FaCalculator, 
    FaFileDownload, 
    FaGraduationCap,
    FaSearch,
    FaStar,
    FaBookmark,
    FaEye,
    FaCopy,
    FaCheck
} from 'react-icons/fa';

const RecursosUtiles = ({ user }) => {
    const [selectedCategory, setSelectedCategory] = useState('Todos');
    const [searchTerm, setSearchTerm] = useState('');
    const [favorites, setFavorites] = useState([]);
    const [copiedUrl, setCopiedUrl] = useState('');

    // Base de datos de recursos organizados por categorías
    const recursos = [
        // Sitios Oficiales
        {
            id: 1,
            title: "Portal de contratos menores a 8 UIT",
            description: "Portal oficial para consultar procesos menores",
            url: "https://prod6.seace.gob.pe/auth-proveedor/",
            category: "Sitios Oficiales",
            tags: ["Contratos menores", "procesos", "pladicop", "oficial"],
            isOfficial: true,
            visits: 1420
        },
        {
            id: 2,
            title: "SEACE - Sistema Electrónico de Contrataciones del Estado",
            description: "Portal oficial de búsqueda de convocatorias",
            url: "https://prod2.seace.gob.pe/seacebus-uiwd-pub/buscadorPublico/buscadorPublico.xhtml",
            category: "Sitios Oficiales",
            tags: ["OECE", "SEACE", "consultas", "oficial"],
            isOfficial: true,
            visits: 2940
        },
        {
            id: 3,
            title: "PAC - Plan Anual de Contrataciones",
            description: "Portal oficial del Plan anual de contrataciones",
            url: "https://prod2.seace.gob.pe/pac3-publico/pages/buscadorPPpublico.xhtml",
            category: "Sitios Oficiales",
            tags: ["PAC", "proveedores", "OECE", "oficial"],
            isOfficial: true,
            visits: 6780
        }

    ];

    const categories = ['Todos', ...new Set(recursos.map(r => r.category))];

    // Filtrar recursos
    const filteredRecursos = recursos.filter(recurso => {
        const matchesCategory = selectedCategory === 'Todos' || recurso.category === selectedCategory;
        const matchesSearch = searchTerm === '' || 
            recurso.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            recurso.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            recurso.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
        
        return matchesCategory && matchesSearch;
    });

    // Funciones de interacción
    const toggleFavorite = (id) => {
        setFavorites(prev => 
            prev.includes(id) 
                ? prev.filter(fav => fav !== id)
                : [...prev, id]
        );
    };

    const copyUrl = async (url, id) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedUrl(id);
            setTimeout(() => setCopiedUrl(''), 2000);
        } catch (err) {
            console.error('Error al copiar URL:', err);
        }
    };

    const getCategoryIcon = (category) => {
        switch(category) {
            case 'Sitios Oficiales': return <FaGlobeAmericas />;

            default: return <FaExternalLinkAlt />;
        }
    };

    return (
        <div className="recursos-container">
            {/* Header */}
            <div className="recursos-header">
                <div className="header-content">
                    <div className="header-text">
                        <h1>
                            <FaExternalLinkAlt className="header-icon" />
                            Recursos Útiles
                        </h1>
                        <p>Repositorio completo de enlaces esenciales para contrataciones públicas</p>
                    </div>
                    <div className="header-stats">
                        <div className="stat">
                            <span className="stat-number">{recursos.length}</span>
                            <span className="stat-label">Recursos</span>
                        </div>
                        <div className="stat">
                            <span className="stat-number">{categories.length - 1}</span>
                            <span className="stat-label">Categorías</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Barra de búsqueda */}
            <div className="search-section">
                <div className="search-container">
                    <div className="search-input-wrapper">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Buscar recursos por nombre, descripción o etiqueta..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>
                </div>
            </div>

            {/* Filtros de categoría */}
            <div className="category-filters">
                {categories.map(category => (
                    <button 
                        key={category}
                        className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(category)}
                    >
                        {category !== 'Todos' && getCategoryIcon(category)}
                        {category} ({category === 'Todos' ? recursos.length : recursos.filter(r => r.category === category).length})
                    </button>
                ))}
            </div>

            {/* Grid de recursos */}
            <div className="recursos-grid">
                {filteredRecursos.map(recurso => (
                    <div key={recurso.id} className="recurso-card">
                        <div className="card-header">
                            <div className="category-badge">
                                {getCategoryIcon(recurso.category)}
                                <span>{recurso.category}</span>
                            </div>
                            <div className="card-actions">
                                <button
                                    className={`favorite-btn ${favorites.includes(recurso.id) ? 'active' : ''}`}
                                    onClick={() => toggleFavorite(recurso.id)}
                                    title="Agregar a favoritos"
                                >
                                    {favorites.includes(recurso.id) ? <FaStar /> : <FaBookmark />}
                                </button>
                            </div>
                        </div>

                        <div className="card-content">
                            <h3 className="recurso-title">{recurso.title}</h3>
                            <p className="recurso-description">{recurso.description}</p>
                            
                            <div className="recurso-tags">
                                {recurso.tags.slice(0, 3).map(tag => (
                                    <span key={tag} className="tag">#{tag}</span>
                                ))}
                                {recurso.tags.length > 3 && (
                                    <span className="tag-more">+{recurso.tags.length - 3}</span>
                                )}
                            </div>

                            <div className="card-footer">
                                <div className="recurso-stats">
                                    {recurso.isOfficial && (
                                        <span className="official-badge">Oficial</span>
                                    )}
                                    <span className="visit-count">
                                        <FaEye /> {recurso.visits.toLocaleString()}
                                    </span>
                                </div>
                                
                                <div className="card-buttons">
                                    <button
                                        className="copy-btn"
                                        onClick={() => copyUrl(recurso.url, recurso.id)}
                                        title="Copiar enlace"
                                    >
                                        {copiedUrl === recurso.id ? <FaCheck /> : <FaCopy />}
                                    </button>
                                    <a
                                        href={recurso.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="visit-btn"
                                    >
                                        <FaExternalLinkAlt />
                                        Visitar
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filteredRecursos.length === 0 && (
                <div className="no-results">
                    <FaSearch className="no-results-icon" />
                    <h3>No se encontraron recursos</h3>
                    <p>Intenta cambiar los filtros o términos de búsqueda</p>
                </div>
            )}

            <style jsx>{`
                .recursos-container {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
                    padding: 0;
                }

                .recursos-header {
                    background: white;
                    padding: 2rem;
                    margin-bottom: 0;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                }

                .header-content {
                    max-width: 1200px;
                    margin: 0 auto;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 2rem;
                }

                .header-text h1 {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    color: #1e293b;
                    font-size: 2.5rem;
                    font-weight: 700;
                    margin: 0 0 0.5rem 0;
                }

                .header-icon {
                    color: #8b5cf6;
                    font-size: 2rem;
                }

                .header-text p {
                    color: #64748b;
                    font-size: 1.1rem;
                    margin: 0;
                    max-width: 500px;
                }

                .header-stats {
                    display: flex;
                    gap: 2rem;
                }

                .stat {
                    text-align: center;
                    padding: 1rem;
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    border-radius: 12px;
                    min-width: 80px;
                }

                .stat-number {
                    display: block;
                    font-size: 2rem;
                    font-weight: 700;
                    color: #8b5cf6;
                    line-height: 1;
                }

                .stat-label {
                    display: block;
                    font-size: 0.875rem;
                    color: #64748b;
                    margin-top: 0.25rem;
                }

                .search-section {
                    background: white;
                    padding: 1.5rem 2rem;
                    border-bottom: 1px solid #e2e8f0;
                }

                .search-container {
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .search-input-wrapper {
                    position: relative;
                    max-width: 600px;
                    margin: 0 auto;
                }

                .search-icon {
                    position: absolute;
                    left: 1rem;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #64748b;
                    font-size: 1rem;
                }

                .search-input {
                    width: 100%;
                    padding: 1rem 1rem 1rem 3rem;
                    border: 2px solid #e2e8f0;
                    border-radius: 25px;
                    font-size: 1rem;
                    transition: all 0.3s ease;
                    background: #f8fafc;
                }

                .search-input:focus {
                    outline: none;
                    border-color: #8b5cf6;
                    background: white;
                    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
                }

                .category-filters {
                    background: white;
                    padding: 1.5rem 2rem 2rem 2rem;
                    margin-bottom: 2rem;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                }

                .category-filters {
                    max-width: 1200px;
                    margin: 0 auto 2rem auto;
                    padding: 1.5rem 2rem;
                    background: white;
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1rem;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }

                .filter-btn {
                    background: rgba(139, 92, 246, 0.1);
                    border: 2px solid transparent;
                    padding: 0.75rem 1.5rem;
                    border-radius: 25px;
                    color: #8b5cf6;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .filter-btn:hover {
                    background: rgba(139, 92, 246, 0.2);
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
                }

                .filter-btn.active {
                    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
                    color: white;
                    border-color: #8b5cf6;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
                }

                .recursos-grid {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 2rem 4rem 2rem;
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
                    gap: 2rem;
                }

                .recurso-card {
                    background: white;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                    transition: all 0.3s ease;
                    border: 1px solid #e2e8f0;
                }

                .recurso-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    border-color: #8b5cf6;
                }

                .card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1.5rem 1.5rem 0 1.5rem;
                }

                .category-badge {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: rgba(139, 92, 246, 0.1);
                    color: #8b5cf6;
                    padding: 0.5rem 1rem;
                    border-radius: 20px;
                    font-size: 0.875rem;
                    font-weight: 600;
                }

                .card-actions {
                    display: flex;
                    gap: 0.5rem;
                }

                .favorite-btn {
                    width: 36px;
                    height: 36px;
                    border: none;
                    border-radius: 50%;
                    background: #f1f5f9;
                    color: #64748b;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s ease;
                }

                .favorite-btn:hover {
                    background: #e2e8f0;
                    transform: scale(1.1);
                }

                .favorite-btn.active {
                    background: #fbbf24;
                    color: white;
                }

                .card-content {
                    padding: 1.5rem;
                }

                .recurso-title {
                    color: #1e293b;
                    font-size: 1.25rem;
                    font-weight: 700;
                    margin: 0 0 0.75rem 0;
                    line-height: 1.4;
                }

                .recurso-description {
                    color: #64748b;
                    font-size: 0.875rem;
                    line-height: 1.6;
                    margin: 0 0 1rem 0;
                }

                .recurso-tags {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                    margin-bottom: 1.5rem;
                }

                .tag {
                    background: #f1f5f9;
                    color: #475569;
                    font-size: 0.75rem;
                    font-weight: 500;
                    padding: 0.25rem 0.75rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }

                .tag-more {
                    background: #8b5cf6;
                    color: white;
                    font-size: 0.75rem;
                    font-weight: 600;
                    padding: 0.25rem 0.75rem;
                    border-radius: 12px;
                }

                .card-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 1rem;
                }

                .recurso-stats {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    font-size: 0.75rem;
                }

                .official-badge {
                    background: #10b981;
                    color: white;
                    padding: 0.25rem 0.75rem;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 0.75rem;
                }

                .visit-count {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    color: #64748b;
                }

                .card-buttons {
                    display: flex;
                    gap: 0.75rem;
                    align-items: center;
                }

                .copy-btn {
                    width: 36px;
                    height: 36px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    background: white;
                    color: #64748b;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s ease;
                }

                .copy-btn:hover {
                    background: #f8fafc;
                    border-color: #8b5cf6;
                    color: #8b5cf6;
                }

                .visit-btn {
                    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
                    color: white;
                    padding: 0.75rem 1.5rem;
                    border-radius: 8px;
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 0.875rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    transition: all 0.3s ease;
                }

                .visit-btn:hover {
                    background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
                    text-decoration: none;
                    color: white;
                }

                .no-results {
                    text-align: center;
                    padding: 4rem 2rem;
                    color: #64748b;
                }

                .no-results-icon {
                    font-size: 3rem;
                    color: #cbd5e1;
                    margin-bottom: 1rem;
                }

                .no-results h3 {
                    font-size: 1.5rem;
                    margin-bottom: 0.5rem;
                    color: #475569;
                }

                @media (max-width: 768px) {
                    .recursos-grid {
                        grid-template-columns: 1fr;
                        padding: 0 1rem 2rem 1rem;
                    }

                    .header-content {
                        flex-direction: column;
                        text-align: center;
                    }

                    .header-text h1 {
                        font-size: 2rem;
                    }

                    .category-filters {
                        padding: 0 1rem;
                        justify-content: center;
                    }

                    .card-footer {
                        flex-direction: column;
                        gap: 1rem;
                        align-items: stretch;
                    }

                    .card-buttons {
                        justify-content: space-between;
                        width: 100%;
                    }

                    .search-input {
                        font-size: 0.875rem;
                    }
                }

                @media (max-width: 480px) {
                    .recursos-grid {
                        gap: 1.5rem;
                    }

                    .card-content {
                        padding: 1rem;
                    }

                    .recursos-header {
                        padding: 1.5rem 1rem;
                    }

                    .category-filters {
                        padding: 1rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default RecursosUtiles;